import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { cx } from "@/lib/tailwind-utils";
import { MemberStatus } from "@/types/enums/membership";
import { MemberTiny } from "@/types/inventory/member";

// Normalized shape for the inline membership badges so callers with
// different membership payloads (PatientInWindow.members,
// PatientSummary.memberships, MemberTiny) can share one renderer.
export interface MembershipBadge {
  name: string;
  status: string;
  textColor: string | null;
}

// Statuses worth surfacing as a badge; the rest (cancelled, expired,
// pending, …) are noise on a patient header.
const DISPLAYABLE_STATUSES: MemberStatus[] = [MemberStatus.ACTIVE, MemberStatus.DELINQUENT, MemberStatus.FROZEN];
export function isDisplayableMember(member: MemberTiny): boolean {
  return DISPLAYABLE_STATUSES.includes(member.status);
}
export function memberTinyToBadge(member: MemberTiny): MembershipBadge {
  return {
    name: member.membership.name,
    status: member.status,
    textColor: member.membership.textColor ?? null
  };
}

/**
 * The membership pill's look: delinquent and frozen take status palettes so they
 * pop whatever the plan's color; everything else uses the plan's configured hue
 * (or the primary tint). Shared so every membership badge on the platform matches.
 */
export function membershipBadgeStyle(status: string, textColor: string | null | undefined): {
  className: string;
  style?: React.CSSProperties;
} {
  const hasStatusPalette = status === "DELINQUENT" || status === "FROZEN";
  return {
    className: cx("shrink-0 rounded px-1.5 py-0.5 text-tiny font-semibold", status === "DELINQUENT" && "bg-status-error-bg text-status-error-text", status === "FROZEN" && "bg-status-warning-bg text-status-warning-text",
    // No status palette + no configured color falls back to the primary-tinted theme badge.
    !hasStatusPalette && !textColor && "bg-primary/10 text-primary"),
    style: !hasStatusPalette && textColor ?
    // 0x1A ≈ 10% alpha — matches the primary/10 visual weight while
    // picking up the membership's configured hue.
    {
      color: textColor,
      backgroundColor: `${textColor}1A`
    } : undefined
  };
}
export function MembershipBadges(t0) {
  const $ = _c(4);
  const {
    badges
  } = t0;
  let t1;
  if ($[0] !== badges) {
    t1 = badges.map(_temp);
    $[0] = badges;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(badge, mi) {
  const hasStatusPalette = badge.status === "DELINQUENT" || badge.status === "FROZEN";
  const {
    className,
    style
  } = membershipBadgeStyle(badge.status, badge.textColor);
  return <span key={`${badge.name}-${mi}`} className={className} style={style} title={hasStatusPalette ? `${badge.name} · ${badge.status.toLowerCase()}` : badge.name}>{badge.name}{hasStatusPalette && <span>{` · ${badge.status.toLowerCase()}`}</span>}</span>;
}